import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock, Loader2, MinusCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import { MetricCard, MetricGrid } from "@/components/MetricCard";
import { StatusBadge } from "@/components/StatusBadge";
import { EnvironmentBadge, ProductionWarningBanner } from "@/components/EnvironmentBadge";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime, formatDuration } from "@/lib/datetime";

const fmtDuration = formatDuration;

export default function SuiteRunPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      api.get(`/suite-runs/${runId}`)
        .then(({ data }) => {
          if (cancelled) return;
          setRun(data);
          if (data.status === "running") {
            timerRef.current = setTimeout(tick, 1500);
          }
        })
        .catch((e) => toast.error(formatApiError(e)));
    };
    tick();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [runId]);

  if (!run) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-10 text-sm text-[#64748B] flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading suite run…
      </div>
    );
  }

  const isRunning = run.status === "running";
  const progressPct = run.total_count
    ? Math.round(100 * (run.passed_count + run.failed_count + run.skipped_count) / run.total_count)
    : 0;

  const projectHref = run.suite?.project_id ? `/projects/${run.suite.project_id}/runs` : "/projects";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-14 flex items-center gap-3">
          <button onClick={() => navigate(projectHref)} className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#0F172A]" data-testid="back-to-runs">
            <ArrowLeft size={14} />
            Back
          </button>
          <span className="text-[#CBD5E1]">·</span>
          <span className="text-sm font-medium text-[#0F172A] truncate">{run.suite?.name || run.name}</span>
          {run.environment && <EnvironmentBadge type={run.environment.type} name={run.environment.name} testId="suite-run-env-badge" />}
          <span className="ml-auto"><StatusBadge status={run.status} /></span>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8" data-testid="suite-run-page">
        {run.environment?.type === "production" && <ProductionWarningBanner />}
        <div className="mb-6">
          <h1 className="font-['Geist'] font-semibold text-[28px] tracking-tight text-[#0F172A] leading-tight">
            Suite run — {run.name}
          </h1>
          <p className="mt-1.5 text-sm text-[#64748B]">
            Triggered {formatDateTime(run.started_at)}
            {run.triggered_by_email ? ` by ${run.triggered_by_email}` : ""}
            {run.duration_ms != null ? ` · ${fmtDuration(run.duration_ms)}` : ""}
            {run.browser ? ` · ${run.browser}` : ""}
          </p>
        </div>

        {isRunning && (
          <div className="surface-card p-5 mb-6" data-testid="suite-run-progress">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="font-medium text-[#0F172A] inline-flex items-center gap-1.5">
                <Loader2 size={14} className="animate-spin text-[#3B82F6]" />
                Running {Math.min(run.current_index + 1, run.total_count)} of {run.total_count}
                {run.current_test_name && <span className="text-[#64748B] font-normal">— {run.current_test_name}</span>}
              </span>
              <span className="text-xs text-[#64748B]">{progressPct}%</span>
            </div>
            <div className="h-1.5 bg-[#F1F5F9] rounded overflow-hidden">
              <div className="h-full bg-[#3B82F6] transition-all" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}

        <MetricGrid columns={4}>
          <MetricCard
            testId="metric-passed"
            label="Passed"
            icon={CheckCircle2}
            value={run.passed_count}
          />
          <MetricCard
            testId="metric-failed"
            label="Failed"
            icon={XCircle}
            value={run.failed_count}
          />
          <MetricCard
            testId="metric-skipped"
            label="Skipped"
            icon={MinusCircle}
            value={run.skipped_count}
          />
          <MetricCard
            testId="metric-duration"
            label="Duration"
            icon={Clock}
            value={fmtDuration(run.duration_ms)}
          />
        </MetricGrid>

        <div className="surface-card">
          <div className="px-5 py-4 border-b border-[#E5E7EB]">
            <h3 className="font-['Geist'] font-semibold text-sm tracking-tight text-[#0F172A]">Test results</h3>
          </div>
          <ul className="divide-y divide-[#F1F5F9]" data-testid="suite-run-tests-list">
            {run.children?.length === 0 ? (
              <li className="p-6 text-sm text-[#64748B]">Waiting for tests to start…</li>
            ) : (
              run.children.map((c) => (
                <li key={c.id} className="px-5 py-3.5 flex items-center gap-3 hover:bg-[#F8FAFC]" data-testid={`suite-child-${c.id}`}>
                  {c.status === "passed" ? (
                    <CheckCircle2 size={15} className="text-[#10B981] shrink-0" />
                  ) : c.status === "failed" ? (
                    <XCircle size={15} className="text-[#EF4444] shrink-0" />
                  ) : c.status === "running" ? (
                    <Loader2 size={15} className="text-[#3B82F6] animate-spin shrink-0" />
                  ) : (
                    <MinusCircle size={15} className="text-[#94A3B8] shrink-0" />
                  )}
                  <Link to={`/runs/${c.id}`} className="flex-1 min-w-0 text-sm font-medium text-[#0F172A] hover:text-[#3B82F6] truncate">
                    {c.test_name}
                  </Link>
                  <span className="text-xs text-[#64748B] shrink-0">{fmtDuration(c.duration_ms)}</span>
                  <StatusBadge status={c.status} />
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
